import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { STATUS_COLOR, STATUS_LABEL, SEGMENTS, segmentOf, activationColor } from './bonusStatus';
import { useBrand } from '../../context/BrandContext';
import { useVipSource } from '../../context/VipSourceContext';

const BONUS_BACKOFFICE_URL = {
  bitstarz: 'https://backoffice.bitstarz-kute.com/backend/bonus_issues',
  cashy:    'https://backoffice.cashy-kaga.com/backend/bonus_issues',
};

function fmtEur(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000000) return '€' + (n / 1000000).toFixed(2) + 'M';
  if (abs >= 1000) return '€' + (n / 1000).toFixed(1) + 'K';
  return '€' + Math.round(n).toLocaleString('en-GB');
}

function fmtInt(n) {
  if (n == null) return '—';
  return Math.round(n).toLocaleString('en-GB');
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// bonus_type is a machine code (free_spins_drop, tournament_reward, …) — make
// it readable for the chart label without losing the grouping.
function prettyType(code) {
  if (!code) return 'Unknown';
  return code.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

// titlesOf groups a bonus_type's individual rows by their title.
function titlesOf(group) {
  const map = new Map();
  for (const r of group.rows) {
    const key = r.title || '(untitled)';
    let t = map.get(key);
    if (!t) {
      t = { title: key, titleOrig: key, rows: [], count: 0, value: 0, freespins: 0, seg: {} };
      for (const s of SEGMENTS) t.seg[s] = 0;
      map.set(key, t);
    }
    t.rows.push(r);
    t.count += 1;
    t.value += r.amount_eur || 0;
    t.freespins += r.freespin_total || 0;
    t.seg[segmentOf(r)] += 1;
  }
  return [...map.values()].sort((a, b) => b.value - a.value);
}

function Tile({ label, value, sub, color }) {
  return (
    <div className="rounded bg-[#0d0f12] p-[10px] text-center">
      <p className="text-[9px] uppercase tracking-[0.4px] text-[#7a8499]">{label}</p>
      <p className="mt-[2px] text-[15px] font-bold" style={{ color: color || '#d8dde8' }}>{value}</p>
      {sub && <p className="mt-[2px] text-[10px] text-[#7a8499]">{sub}</p>}
    </div>
  );
}

// FunnelBar — single labelled progress bar row.
function FunnelBar({ label, value, total, color, sub }) {
  const pct = total > 0 ? Math.max((value / total) * 100, value > 0 ? 1 : 0) : 0;
  return (
    <div>
      <div className="mb-[3px] flex items-baseline justify-between gap-2">
        <span className="text-[11px] text-[#d8dde8]">{label}</span>
        <span className="text-[11px] font-semibold tabular-nums" style={{ color }}>{fmtInt(value)}</span>
      </div>
      <div className="h-[10px] overflow-hidden rounded-[3px] bg-[#0d0f12]">
        <div className="h-full rounded-[3px] transition-[width]" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      {sub && <p className="mt-[2px] text-[10px] text-[#5a6477]">{sub}</p>}
    </div>
  );
}

// LeakRow — small annotation showing bonuses that "leaked" at a stage.
function LeakRow({ items }) {
  const visible = items.filter((it) => it.value > 0);
  if (!visible.length) return null;
  return (
    <div className="flex flex-wrap gap-x-[10px] gap-y-[1px] pl-[8px] border-l-2 border-[#1f2430]">
      {visible.map((it) => (
        <span key={it.label} className="text-[10px]" style={{ color: it.color }}>
          {fmtInt(it.value)} {it.label}
        </span>
      ))}
    </div>
  );
}

function Funnel({ totals }) {
  const { bonuses, activated, won, lost, stillActive, canceledBefore, canceledAfter, expired } = totals;
  const pctOf = (v) => bonuses > 0 ? ` · ${Math.round((v / bonuses) * 100)}% of issued` : '';
  return (
    <div className="flex flex-col gap-[8px]">
      {/* Stage 1: Issued */}
      <FunnelBar label="Issued (total)" value={bonuses} total={bonuses} color="#7a8499" />

      {/* Leaks before activation */}
      <LeakRow items={[
        { label: 'expired unused', value: expired, color: STATUS_COLOR.expired },
        { label: 'canceled before activation', value: canceledBefore, color: STATUS_COLOR.canceled },
      ]} />

      {/* Stage 2: Activated */}
      <FunnelBar
        label={`Activated${pctOf(activated)}`}
        value={activated} total={bonuses} color={STATUS_COLOR.active}
      />

      {/* Leaks after activation */}
      <LeakRow items={[
        { label: 'canceled mid-wager', value: canceledAfter, color: STATUS_COLOR.canceled },
        { label: 'still active', value: stillActive, color: STATUS_COLOR.active },
      ]} />

      {/* Stage 3: Completed — split won vs lost */}
      <div>
        <div className="mb-[3px] flex items-baseline justify-between gap-2">
          <span className="text-[11px] text-[#d8dde8]">
            Completed{pctOf(won + lost)}
          </span>
          <span className="text-[11px] font-semibold tabular-nums text-[#d8dde8]">{fmtInt(won + lost)}</span>
        </div>
        {/* Segmented bar: won + lost */}
        <div className="h-[10px] overflow-hidden rounded-[3px] bg-[#0d0f12] flex">
          {bonuses > 0 && won > 0 && (
            <div style={{ width: `${(won / bonuses) * 100}%`, backgroundColor: STATUS_COLOR.won }} />
          )}
          {bonuses > 0 && lost > 0 && (
            <div style={{ width: `${(lost / bonuses) * 100}%`, backgroundColor: STATUS_COLOR.lost }} />
          )}
        </div>
        {(won > 0 || lost > 0) && (
          <div className="mt-[3px] flex gap-[10px]">
            {won > 0 && (
              <span className="text-[10px]" style={{ color: STATUS_COLOR.won }}>
                {fmtInt(won)} wagered through
              </span>
            )}
            {lost > 0 && (
              <span className="text-[10px]" style={{ color: STATUS_COLOR.lost }}>
                {fmtInt(lost)} lost funds
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BarTooltip({ active, payload, xMetric }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const meta = row._segMeta || {};
  return (
    <div className="max-w-[280px] rounded border border-[#2a3042] bg-[#0d0f12] p-2 text-[11px]">
      <p className="mb-[4px] text-[11px] font-bold text-[#d8dde8]">{row.label}</p>
      {SEGMENTS.filter((seg) => meta[seg] && meta[seg].count > 0).map((seg) => (
        <div key={seg} className="flex justify-between gap-[6px]" style={{ color: STATUS_COLOR[seg] }}>
          <span>{STATUS_LABEL[seg]}</span>
          <span style={{ color: '#d8dde8' }}>
            {fmtInt(meta[seg].count)} · {fmtEur(meta[seg].value)} · {fmtInt(meta[seg].freespins)} FS
          </span>
        </div>
      ))}
      <p className="mt-[4px] text-[9px] text-[#5a6477]">
        Sorted by {xMetric === 'value' ? 'face value' : 'count'} · click a bar to drill in
      </p>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <span className="text-[11px] text-[#d8dde8]">
      <span className="text-[#7a8499]">{label}: </span>{value}
    </span>
  );
}

// Thin proportional status bar — the count mix of one title.
function MiniStatusStrip({ seg }) {
  const total = SEGMENTS.reduce((s, k) => s + (seg[k] || 0), 0);
  if (total <= 0) return null;
  return (
    <div className="mt-[2px] flex h-[5px] overflow-hidden rounded-[2px]">
      {SEGMENTS.map((k) => (seg[k] > 0
        ? <div key={k} style={{ flexGrow: seg[k], backgroundColor: STATUS_COLOR[k] }} />
        : null))}
    </div>
  );
}

// Side panel, level 1 — the titles inside a clicked bonus_type.
function TitleList({ group, titles, onPick, onClose }) {
  return (
    <div className="rounded border border-[#2a3042] bg-[#0d0f12]">
      <div className="flex items-center justify-between gap-2 border-b border-[#1f2430] px-[14px] py-[8px]">
        <div className="min-w-0">
          <p className="text-[12px] font-bold text-[#d8dde8]">{group.label}</p>
          <p className="text-[10px] text-[#7a8499]">
            {titles.length} title{titles.length === 1 ? '' : 's'} — click one for per-row detail
          </p>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 cursor-pointer text-[11px] text-[#7a8499] hover:text-[#d4a843]"
        >
          ✕ close
        </button>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        {titles.map((t) => (
          <button
            key={t.title}
            onClick={() => onPick(t.title)}
            className="w-full cursor-pointer border-b border-[#1f2430] px-[14px] py-[8px] text-left last:border-b-0 hover:bg-[#1f2430]"
          >
            <div className="flex justify-between gap-2">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold text-[#d8dde8]">
                {t.titleOrig || t.title}
              </span>
              <span className="shrink-0 text-[12px] text-[#5a6477]">›</span>
            </div>
            <p className="text-[10px] text-[#7a8499]">
              {fmtInt(t.count)} bonus{t.count === 1 ? '' : 'es'} · {fmtEur(t.value)}
              {t.freespins > 0 ? ` · ${fmtInt(t.freespins)} FS` : ''}
            </p>
            <MiniStatusStrip seg={t.seg} />
          </button>
        ))}
      </div>
    </div>
  );
}

// Side panel, level 2 — the raw rows behind one title.
function DetailPanel({ titleGroup, onBack, onClose, bonusBackofficeUrl }) {
  if (!titleGroup) return null;
  return (
    <div className="rounded border border-[#2a3042] bg-[#0d0f12]">
      <div className="flex items-center justify-between gap-2 border-b border-[#1f2430] px-[14px] py-[8px]">
        <div className="flex min-w-0 items-center gap-[6px]">
          <button
            onClick={onBack}
            className="shrink-0 cursor-pointer text-[13px] text-[#7a8499] hover:text-[#d4a843]"
          >
            ←
          </button>
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-bold text-[#d8dde8]">
            {titleGroup.titleOrig || titleGroup.title}
          </span>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 cursor-pointer text-[11px] text-[#7a8499] hover:text-[#d4a843]"
        >
          ✕
        </button>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        {titleGroup.rows.map((r) => {
          const seg = segmentOf(r);
          return (
            <div key={r.id} className="border-b border-[#1f2430] px-[14px] py-[8px] last:border-b-0">
              {r.title_orig && (
                <p className="mb-[3px] text-[11px] text-[#d8dde8] font-medium truncate">{r.title_orig}</p>
              )}
              <div className="mb-[4px] flex items-center gap-[4px]">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLOR[seg] }} />
                <span className="text-[11px] font-semibold" style={{ color: STATUS_COLOR[seg] }}>{STATUS_LABEL[seg]}</span>
                {r.action && <span className="text-[10px] text-[#7a8499]">· {r.action}</span>}
              </div>
              <div className="flex flex-wrap gap-x-[6px] gap-y-[2px]">
                <KV label="Value" value={fmtEur(r.amount_eur)} />
                <KV label="Free spins" value={fmtInt(r.freespin_total)} />
                <KV label="Wager factor" value={r.wager_factor ? r.wager_factor + '×' : '—'} />
                {r.strategy && <KV label="Strategy" value={r.strategy} />}
              </div>
              <p className="mt-[3px] text-[10px] text-[#5a6477]">
                Created {fmtDate(r.created_at)}
                {r.activated_at ? ` · Activated ${fmtDate(r.activated_at)}` : ''}
                {r.freespin_provider ? ` · ${r.freespin_provider}` : ''}
              </p>
              {r.id && !bonusBackofficeUrl && (
                <p className="mt-[3px] text-[10px] text-[#5a6477]">#{r.id}</p>
              )}
              {r.id && bonusBackofficeUrl && (
                <a
                  href={`${bonusBackofficeUrl}/${r.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-[3px] text-[10px] text-[#5a6477] hover:text-[#d4a843]"
                  style={{ display: 'inline-block' }}
                >
                  BO #{r.id} ↗
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BonusProfile({ trajectory, bonusTypes, daily, zStart, zEnd }) {
  const { activeBrand } = useBrand() || {};
  const { backofficeLinks } = useVipSource();
  // null when the source has no back office links: bonus IDs then show as plain text
  const bonusBackofficeUrl = backofficeLinks ? (BONUS_BACKOFFICE_URL[activeBrand] || BONUS_BACKOFFICE_URL.bitstarz) : null;

  const [xMetric, setXMetric] = useState('count');
  const [expandedType, setExpandedType] = useState(null);
  const [expandedTitle, setExpandedTitle] = useState(null);

  // Filter to the active zoom window when daily + indices are provided.
  const rows = useMemo(() => {
    const all = bonusTypes || [];
    if (!daily?.length || zStart == null || zEnd == null) return all;
    const minT = new Date(daily[Math.max(0, zStart)].day_date).getTime();
    const maxT = new Date(daily[Math.min(zEnd, daily.length - 1)].day_date).getTime() + 86399999;
    return all.filter((r) => {
      if (!r.created_at) return false;
      const t = new Date(r.created_at).getTime();
      return t >= minT && t <= maxT;
    });
  }, [bonusTypes, daily, zStart, zEnd]);

  const totals = useMemo(() => {
    const t = {
      bonuses: 0, value: 0, freespins: 0,
      activated: 0,
      won: 0, lost: 0,
      stillActive: 0,
      canceledBefore: 0,  // canceled before activation
      canceledAfter: 0,   // canceled after activation (mid-wager)
      expired: 0,
    };
    for (const r of rows) {
      t.bonuses += 1;
      t.value += r.amount_eur || 0;
      t.freespins += r.freespin_total || 0;
      const wasActivated = !!r.activated_at;
      if (wasActivated) t.activated += 1;
      if (r.action === 'wager_done') t.won += 1;
      else if (r.action === 'lost') t.lost += 1;
      else if (r.action === 'handle_bets') t.stillActive += 1;
      else if (r.action === 'canceled') {
        if (wasActivated) t.canceledAfter += 1;
        else t.canceledBefore += 1;
      } else if (r.action === 'expired') t.expired += 1;
    }
    return t;
  }, [rows]);

  // One bar per bonus_type; drill into titles, then raw rows, in the side panel.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.bonus_type || 'unknown';
      let g = map.get(key);
      if (!g) {
        g = { bonusType: key, label: prettyType(key), rows: [], _segMeta: {} };
        for (const seg of SEGMENTS) g._segMeta[seg] = { count: 0, value: 0, freespins: 0 };
        map.set(key, g);
      }
      g.rows.push(r);
      const seg = segmentOf(r);
      g._segMeta[seg].count += 1;
      g._segMeta[seg].value += r.amount_eur || 0;
      g._segMeta[seg].freespins += r.freespin_total || 0;
    }
    const arr = [...map.values()].map((g) => {
      const item = { bonusType: g.bonusType, label: g.label, rows: g.rows, _segMeta: g._segMeta };
      let total = 0;
      for (const seg of SEGMENTS) {
        const v = xMetric === 'value' ? g._segMeta[seg].value : g._segMeta[seg].count;
        item[seg] = v;
        total += v;
      }
      item._total = total;
      return item;
    });
    arr.sort((a, b) => b._total - a._total);
    return arr;
  }, [rows, xMetric]);

  const activationRate = totals.bonuses > 0 ? totals.activated / totals.bonuses : null;
  const expiredPct = totals.bonuses > 0 ? Math.round((totals.expired / totals.bonuses) * 100) : 0;
  const canceled = totals.canceledBefore + totals.canceledAfter;
  const finished = totals.won + totals.lost;
  const ratioPct = trajectory && trajectory.bonus_to_stake_ratio != null
    ? trajectory.bonus_to_stake_ratio * 100 : null;
  const chartHeight = Math.max(120, groups.length * 38 + 24);

  const expandedGroup = groups.find((g) => g.bonusType === expandedType) || null;
  const titleList = expandedGroup ? titlesOf(expandedGroup) : [];
  const expandedTitleGroup = expandedTitle ? (titleList.find((t) => t.title === expandedTitle) || null) : null;

  const closeAll = () => { setExpandedType(null); setExpandedTitle(null); };

  return (
    <div className="rounded border border-[#2a3042] bg-[#13161b] p-4">
      <p className="mb-[12px] text-[14px] font-bold text-[#d8dde8]">Bonus profile</p>

      {/* headline tiles */}
      <div className="mb-2 grid grid-cols-3 gap-2">
        <Tile label="Bonuses issued" value={fmtInt(totals.bonuses)} />
        <Tile label="Bonus value" value={fmtEur(totals.value)} />
        <Tile label="Free spins" value={fmtInt(totals.freespins)} />
      </div>
      <div className="mb-2 grid grid-cols-3 gap-2">
        <Tile
          label="Activation rate"
          value={activationRate != null ? Math.round(activationRate * 100) + '%' : '—'}
          color={activationColor(activationRate)}
        />
        <Tile
          label="Expired (unused)"
          value={fmtInt(totals.expired)}
          sub={`${expiredPct}% of issued`}
          color={totals.expired > 0 ? STATUS_COLOR.expired : undefined}
        />
        <Tile
          label="Bonus ÷ turnover"
          value={ratioPct != null ? ratioPct.toFixed(1) + '%' : '—'}
          color={ratioPct != null && ratioPct > 20 ? '#FFD22F' : undefined}
        />
      </div>
      <div className="mb-4 grid grid-cols-3 gap-2">
        <Tile
          label="Lost funds"
          value={fmtInt(totals.lost)}
          sub={totals.bonuses > 0 ? `${Math.round((totals.lost / totals.bonuses) * 100)}% of issued` : undefined}
          color={totals.lost > 0 ? STATUS_COLOR.lost : undefined}
        />
        <Tile
          label="Canceled"
          value={fmtInt(canceled)}
          sub={canceled > 0 ? `${totals.canceledBefore} before · ${totals.canceledAfter} mid-wager` : undefined}
          color={canceled > 0 ? STATUS_COLOR.canceled : undefined}
        />
        <Tile label="Completed" value={fmtInt(finished)} sub="won + lost funds" />
      </div>

      {rows.length === 0 ? (
        <p className="text-[12px] text-[#7a8499]">
          No bonus activity for this player in the last 6 months.
        </p>
      ) : (
        <>
          {/* activation funnel */}
          <p className="mb-[6px] text-[11px] text-[#7a8499]">Activation funnel</p>
          <div className="mb-4">
            <Funnel totals={totals} />
          </div>

          {/* bonus types stacked bar */}
          <div className="mb-[6px] flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-[#7a8499]">
              Bonus types by status — click a bar, then a title, to drill in
            </p>
            <div className="flex gap-[4px]">
              {[{ k: 'count', l: 'Count' }, { k: 'value', l: 'Value (EUR)' }].map((o) => (
                <button
                  key={o.k}
                  onClick={() => setXMetric(o.k)}
                  className="select-none cursor-pointer rounded px-[10px] py-[3px] text-[10px] border"
                  style={{
                    borderColor: xMetric === o.k ? '#d4a843' : '#2a3042',
                    color: xMetric === o.k ? '#d4a843' : '#7a8499',
                    backgroundColor: xMetric === o.k ? '#d4a84318' : 'transparent',
                  }}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-4">
            {/* left — drill-down detail for the clicked bar */}
            <div className="min-w-0 flex-[1_1_280px]">
              {expandedTitleGroup ? (
                <DetailPanel
                  titleGroup={expandedTitleGroup}
                  onBack={() => setExpandedTitle(null)}
                  onClose={closeAll}
                  bonusBackofficeUrl={bonusBackofficeUrl}
                />
              ) : expandedGroup ? (
                <TitleList
                  group={expandedGroup}
                  titles={titleList}
                  onPick={setExpandedTitle}
                  onClose={closeAll}
                />
              ) : (
                <div className="rounded border border-dashed border-[#2a3042] p-4 text-center">
                  <p className="text-[11px] text-[#5a6477]">
                    Click a bar to see the bonus titles within that type.
                  </p>
                </div>
              )}
            </div>

            {/* right — stacked bar chart */}
            <div className="min-w-0 flex-[2_1_360px]">
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart layout="vertical" data={groups} margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#1f2430" horizontal={false} />
                  <XAxis
                    type="number" tick={{ fontSize: 9, fill: '#7a8499' }}
                    tickLine={false} axisLine={{ stroke: '#2a3042' }}
                    tickFormatter={xMetric === 'value' ? fmtEur : fmtInt}
                  />
                  <YAxis
                    type="category" dataKey="label" width={120}
                    tick={{ fontSize: 10, fill: '#d8dde8' }} tickLine={false} axisLine={{ stroke: '#2a3042' }}
                    tickFormatter={(v) => (v.length > 18 ? v.slice(0, 17) + '…' : v)}
                  />
                  <Tooltip content={<BarTooltip xMetric={xMetric} />} cursor={{ fill: '#ffffff08' }} />
                  {SEGMENTS.map((seg) => (
                    <Bar
                      key={seg} dataKey={seg} stackId="a" fill={STATUS_COLOR[seg]}
                      cursor="pointer" isAnimationActive={false}
                      onClick={(d) => {
                        setExpandedTitle(null);
                        setExpandedType((cur) => (cur === d.bonusType ? null : d.bonusType));
                      }}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>

              {/* status legend */}
              <div className="mt-[4px] flex flex-wrap gap-[6px]">
                {SEGMENTS.map((seg) => (
                  <div key={seg} className="flex items-center gap-[4px]">
                    <div className="h-2 w-2 rounded-[2px]" style={{ backgroundColor: STATUS_COLOR[seg] }} />
                    <span className="text-[10px] text-[#7a8499]">{STATUS_LABEL[seg]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-2 text-[10px] italic text-[#5a6477]">
            Bonus value is face value at activation, not net of wagering. "Lost" is a normal bonus
            outcome, not a player loss. Avg wager factor is shown per row in the detail panel — it's
            not meaningful averaged across bonus types.
          </p>
        </>
      )}
    </div>
  );
}
